import { Folder, Pin, Users, Sparkles, MessageCircle, Globe } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Collection } from '@/hooks/useCollections';
import { useLongPress } from '@/hooks/useLongPress';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { getAvatarUrl } from '@/lib/avatars';
import { FollowerAvatars } from './FollowerAvatars';

// Generate unique gradient colors based on collection ID
function generateCollectionGradient(collectionId: string): string {
  let hash = 0;
  for (let i = 0; i < collectionId.length; i++) {
    const char = collectionId.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  
  const baseHue = Math.abs(hash % 360);
  const hue1 = baseHue;
  const hue2 = (baseHue + 40) % 360;
  const hue3 = (baseHue + 180) % 360;
  
  const patterns = [
    `linear-gradient(135deg, hsl(${hue1}, 70%, 45%) 0%, hsl(${hue2}, 60%, 35%) 50%, hsl(${hue3}, 50%, 25%) 100%)`,
    `linear-gradient(160deg, hsl(${hue1}, 80%, 50%) 0%, hsl(${hue2}, 70%, 40%) 40%, hsl(${hue3}, 60%, 30%) 100%)`,
    `radial-gradient(ellipse at 20% 80%, hsl(${hue1}, 75%, 50%) 0%, hsl(${hue2}, 65%, 35%) 50%, hsl(${hue3}, 55%, 25%) 100%)`,
    `linear-gradient(45deg, hsl(${hue1}, 65%, 40%) 0%, hsl(${hue2}, 75%, 45%) 33%, hsl(${hue3}, 60%, 35%) 66%, hsl(${(hue1 + 20) % 360}, 70%, 40%) 100%)`,
    `conic-gradient(from 45deg at 30% 70%, hsl(${hue1}, 70%, 45%), hsl(${hue2}, 65%, 40%), hsl(${hue3}, 60%, 35%), hsl(${hue1}, 70%, 45%))`,
  ];
  
  return patterns[Math.abs(hash % patterns.length)];
}

interface FollowerProfile {
  user_id: string;
  display_name: string | null;
  avatar_url: string | null;
}

interface CollectionActivity {
  unreadMessages: number;
  newItemsCount: number;
}

interface CollectionGridCardProps {
  collection: Collection & { 
    owner_name?: string; 
    owner_avatar?: string; 
  };
  onSelect: (collection: Collection) => void;
  onLongPress: (collection: Collection) => void;
  showOwner?: boolean;
  /** Follower profiles to display as stacked avatars */
  followers?: FollowerProfile[];
  /** Activity data (unread messages, new items) */
  activity?: CollectionActivity;
}

// Check if date is within last 48 hours
function isRecentlyAdded(dateString: string | null | undefined): boolean {
  if (!dateString) return false;
  const date = new Date(dateString);
  const now = new Date();
  const hoursDiff = (now.getTime() - date.getTime()) / (1000 * 60 * 60);
  return hoursDiff <= 48;
}

const colorClasses: Record<string, string> = {
  primary: 'bg-primary',
  red: 'bg-red-500',
  orange: 'bg-orange-500',
  yellow: 'bg-yellow-500',
  green: 'bg-green-500',
  blue: 'bg-blue-500',
  purple: 'bg-purple-500',
  pink: 'bg-pink-500',
};

interface ImageMosaicProps {
  images: string[];
  itemCount: number;
  color: string;
  coverImage?: string | null;
  collectionId: string;
}

function ImageMosaic({ images, itemCount, color, coverImage, collectionId }: ImageMosaicProps) {
  // If there's a cover image, use it as full cover
  if (coverImage) {
    return (
      <img 
        src={coverImage} 
        alt="" 
        className="w-full h-full object-cover"
      />
    );
  }

  // 0 images - show colored background with folder icon
  if (images.length === 0) {
    return (
      <div 
        className="w-full h-full flex items-center justify-center"
        style={{ background: generateCollectionGradient(collectionId) }}
      >
        <Folder className="w-10 h-10 text-white/60" />
      </div>
    );
  }

  // 1 image - full cover
  if (images.length === 1) {
    return (
      <img 
        src={images[0]} 
        alt="" 
        className="w-full h-full object-cover"
      />
    );
  }

  // 2-3 images - asymmetric layout (first image larger on left)
  if (images.length <= 3) {
    return (
      <div className="w-full h-full grid grid-cols-2 gap-0.5">
        <img 
          src={images[0]} 
          alt="" 
          className="w-full h-full object-cover row-span-2"
        />
        <div className="flex flex-col gap-0.5 h-full">
          {images.slice(1).map((img, i) => (
            <div key={i} className="flex-1 min-h-0">
              <img 
                src={img} 
                alt="" 
                className="w-full h-full object-cover"
              />
            </div>
          ))}
          {images.length === 2 && (
            <div className={cn(
              "flex-1 min-h-0 flex items-center justify-center",
              colorClasses[color] || 'bg-primary'
            )}>
              <Folder className="w-6 h-6 text-white/60" />
            </div>
          )}
        </div>
      </div>
    );
  }

  // 4+ images - 2x2 grid with overflow indicator
  return (
    <div className="w-full h-full grid grid-cols-2 grid-rows-2 gap-0.5">
      {images.slice(0, 4).map((img, i) => (
        <div key={i} className="relative overflow-hidden">
          <img 
            src={img} 
            alt="" 
            className="w-full h-full object-cover"
          />
          {i === 3 && itemCount > 4 && (
            <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
              <span className="text-white font-bold text-sm">+{itemCount - 4}</span>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

export function CollectionGridCard({ collection, onSelect, onLongPress, showOwner = false, followers = [], activity }: CollectionGridCardProps) {
  const longPressHandlers = useLongPress({
    onLongPress: () => onLongPress(collection),
    onTap: () => onSelect(collection),
    hapticStyle: 'medium',
  });

  const previewImages = collection.preview_images || [];
  const itemCount = collection.item_count || 0;
  const ownerName = collection.owner_name;
  const ownerAvatar = collection.owner_avatar;
  const ownerInitial = ownerName?.charAt(0)?.toUpperCase() || '?';
  const hasRecentActivity = isRecentlyAdded(collection.latest_item_added_at);
  
  // Activity indicators for collab collections
  const hasUnreadMessages = activity?.unreadMessages && activity.unreadMessages > 0;
  const hasNewItems = activity?.newItemsCount && activity.newItemsCount > 0;
  const hasAnyActivity = hasUnreadMessages || hasNewItems;

  return (
    <div
      {...longPressHandlers}
      className={cn(
        "relative aspect-[3/4] rounded-2xl overflow-hidden bg-surface-2 border cursor-pointer select-none active:scale-[0.98] transition-transform",
        "border-border"
      )}
    >
      {/* Image Mosaic */}
      <div className="absolute inset-0">
        <ImageMosaic 
          images={previewImages} 
          itemCount={itemCount}
          color={collection.color}
          coverImage={collection.cover_image_url}
          collectionId={collection.id}
        />
      </div>

      {/* Gradient overlay for text legibility */}
      <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />

      {/* Owner avatar badge - top left (for followed collections) */}
      {showOwner && ownerName && (
        <div className="absolute top-2 left-2 flex items-center gap-1.5 px-1.5 py-1 rounded-full bg-black/50 backdrop-blur-sm">
          <Avatar className="w-5 h-5 border border-white/30">
            <AvatarImage src={getAvatarUrl(ownerAvatar, collection.user_id)} alt={ownerName} />
            <AvatarFallback className="bg-muted" />
          </Avatar>
          <span className="text-[11px] font-medium text-white pr-1 max-w-[70px] truncate">
            {ownerName}
          </span>
        </div>
      )}

      {/* Pin badge - top left (when not showing owner) */}
      {!showOwner && collection.pinned_at && (
        <div className="absolute top-2 left-2 w-7 h-7 rounded-full bg-black/50 backdrop-blur-sm flex items-center justify-center">
          <Pin className="w-3.5 h-3.5 text-white" />
        </div>
      )}

      {/* Public badge - top left (when not pinned and not showing owner) */}
      {!showOwner && !collection.pinned_at && collection.is_public && (
        <div className="absolute top-2 left-2 px-2 py-1 rounded-full bg-emerald-500/90 backdrop-blur-sm flex items-center gap-1">
          <Globe className="w-3 h-3 text-white" />
          <span className="text-xs font-medium text-white">Public</span>
        </div>
      )}

      {/* Recently Added badge - top right (when no collab badge) */}
      {hasRecentActivity && !collection.is_collaborative && (
        <div className="absolute top-2 right-2 px-2 py-1 rounded-full bg-amber-500/90 backdrop-blur-sm flex items-center gap-1">
          <Sparkles className="w-3 h-3 text-white" />
          <span className="text-xs font-medium text-white">New</span>
        </div>
      )}

      {/* Group badge with activity indicators - top right */}
      {collection.is_collaborative && (
        <div className="absolute top-2 right-2 flex flex-col items-end gap-1">
          {/* Main group badge with activity dot */}
          <div className="relative px-2 py-1 rounded-full bg-primary/90 backdrop-blur-sm flex items-center gap-1.5">
            <Users className="w-3 h-3 text-white" />
            <span className="text-xs font-medium text-white">Group</span>
          </div>
          
          {/* Activity badges */}
          {hasUnreadMessages === true && activity?.unreadMessages != null && activity.unreadMessages > 0 && (
            <div className="px-2 py-1 rounded-full bg-blue-500/90 backdrop-blur-sm flex items-center gap-1">
              <MessageCircle className="w-3 h-3 text-white" />
              <span className="text-xs font-medium text-white">{activity?.unreadMessages} new</span>
            </div>
          )}
          {hasNewItems === true && activity?.newItemsCount != null && activity.newItemsCount > 0 && (
            <div className="px-2 py-1 rounded-full bg-amber-500/90 backdrop-blur-sm flex items-center gap-1">
              <Sparkles className="w-3 h-3 text-white" />
              <span className="text-xs font-medium text-white">{activity?.newItemsCount} added</span>
            </div>
          )}
          {/* Show New badge if recently added but no specific activity data */}
          {hasAnyActivity !== true && hasRecentActivity === true && (
            <div className="px-2 py-1 rounded-full bg-amber-500/90 backdrop-blur-sm flex items-center gap-1">
              <Sparkles className="w-3 h-3 text-white" />
              <span className="text-xs font-medium text-white">New</span>
            </div>
          )}
        </div>
      )}

      {/* Bottom content overlay */}
      <div className="absolute inset-x-0 bottom-0 p-3">
        <div className="flex items-end justify-between gap-2">
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-white truncate text-sm">
              {collection.name}
            </p>
            <p className="text-xs text-white/70 mt-0.5">
              {itemCount} {itemCount === 1 ? 'item' : 'items'}
            </p>
          </div>
          {/* Follower avatars for collaborative collections */}
          {collection.is_collaborative && followers && followers.length > 0 && (
            <FollowerAvatars 
              followers={followers} 
              maxVisible={3} 
              size="sm" 
              overlay 
            />
          )}
        </div>
      </div>
    </div>
  );
}
